import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createAppTheme } from '../../theme/theme';

type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'system';

interface ThemeContextType {
  /** The resolved theme actually on screen; 'system' is never returned here. */
  themeMode: ThemeMode;
  /** What the viewer picked: an explicit theme, or 'system' to follow the OS. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles system → light → dark → system. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'cerebellum-hub:theme-preference';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const useThemeToggle = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeToggle must be used within WorkingThemeProvider');
  }
  return context;
};

// Following the OS is the default; light and dark are explicit viewer choices.
// Only an explicit choice is ever written to storage, so an unset visitor keeps
// tracking their system setting instead of being pinned to whatever they saw first.
const readStoredPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'system';
  } catch {
    return 'system';
  }
};

const readSystemMode = (): ThemeMode => {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

interface WorkingThemeProviderProps {
  children: React.ReactNode;
}

export const WorkingThemeProvider: React.FC<WorkingThemeProviderProps> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(readSystemMode);

  // While on 'system', an OS theme switch has to move the page live.
  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', handleChange);
    setSystemMode(query.matches ? 'dark' : 'light');
    return () => query.removeEventListener('change', handleChange);
  }, []);

  const themeMode: ThemeMode = preference === 'system' ? systemMode : preference;

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  // The design-system tokens switch on `data-theme` on <html>; MUI's palette
  // switches on themeMode. Both must move together. The inline script in
  // index.html sets the same attribute before first paint.
  useEffect(() => {
    const root = document.documentElement;
    if (themeMode === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }

    // Mobile browser chrome is painted from <meta name="theme-color">, which is
    // static markup; read the page surface back off the tokens that the
    // attribute above just switched, so the address bar tracks the theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    const surface = getComputedStyle(root).getPropertyValue('--surface-page').trim();
    if (meta && surface) {
      meta.setAttribute('content', surface);
    }
  }, [themeMode]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      if (next === 'system') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // Storage can be unavailable (private mode); the theme still applies.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference(preference === 'system' ? 'light' : preference === 'light' ? 'dark' : 'system');
  }, [preference, setPreference]);

  const value = useMemo(
    () => ({ themeMode, preference, setPreference, toggleTheme }),
    [themeMode, preference, setPreference, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default WorkingThemeProvider;
